import { useMemo } from 'react';

import { useAssigneeOptionsList } from 'hooks/useAssigneeOptionsList';

import { useProjectTreeStore } from 'store/projectContract.store';

import { useQueryActivityCodesByVersionAndWidgetId } from 'api/queries/activityCodesByVersion.query';
import { useQueryClustersList } from 'api/queries/clustersList.query';
import { useQueryGetResourceListForChart } from 'api/queries/getResourceListForChart.query';
import { useQueryGetSingleMilestoneFragnet } from 'api/queries/getSingleMilestoneFragnet.query';
import { useQueryGetSingleWbs } from 'api/queries/getWbs.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';

import { generateFilterByType } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills.utils';
import { IFilter } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';

const activityCodesNames = [FilterNames.ACTIVITY_CODES, FilterNames.ACTIVITY_CODES_EXCLUDE];
const fragnetMilestoneNames = [FilterNames.FRAGNET_MILESTONE_ID_CARD_BOARD, FilterNames.FRAGNET_SEARCH];
const wbsNames = [FilterNames.WBS_SEARCH];

interface IUseActivityCodesHook {
    widgetId?: string;
    filter?: {
        filters: IFilter[];
    };
    latestVersionId?: number;
    source?: unknown;
}

export const useGlobalFiltersHook = ({
    widgetId,
    filter,
    latestVersionId,
    source = null,
}: IUseActivityCodesHook): { generateFilters: IFilter[]; pillsList: unknown[] } => {
    const { activityCodes, milestoneFragnet, wbs, resourceOptions, assigneeOptionsList, clusters } = useGetFilterValues(
        {
            latestVersionId,
            widgetId,
            filter,
            source,
        },
    );

    // expects list of object with name and data
    // [
    //      { name: 'FILTER_NAME', data: [100,200] },
    //      { name: 'ANOTHER_FILTER_NAME', data: ['my_value'] }
    // ]
    const generateFilters =
        widgetId && filter
            ? filter?.filters.map((item) =>
                  generateFilterByType(
                      item,
                      activityCodes,
                      milestoneFragnet,
                      wbs,
                      resourceOptions,
                      assigneeOptionsList,
                      clusters,
                  ),
              )
            : [];

    // new & improved mechanism, expect an object with key value structure:
    // {
    //      filterName: [100,200],
    //      anotherFilterName: 'my_value'
    // }
    const pillsList = useMemo(() => {
        if (widgetId && source) {
            const list = getFiltersData(source);
            return list.map((item) =>
                generateFilterByType(
                    item,
                    activityCodes,
                    milestoneFragnet,
                    wbs,
                    resourceOptions,
                    assigneeOptionsList,
                    clusters,
                ),
            );
        } else {
            return [];
        }
    }, [source, widgetId, activityCodes, milestoneFragnet, wbs, resourceOptions]);

    return { generateFilters, pillsList };
};

const useGetFilterValues = ({ latestVersionId, widgetId, filter, source }) => {
    const { project } = useProjectTreeStore();
    const { options: assigneeOptionsList } = useAssigneeOptionsList();
    const { data: clusters } = useQueryClustersList({
        idList: latestVersionId ? [latestVersionId] : [],
        projectId: project?.id,
        compare: 'ITSELF',
    });
    const { data: activityCodes } = useQueryActivityCodesByVersionAndWidgetId({
        latestVersion: latestVersionId,
        widgetId: widgetId,
        hasActivityCode:
            source?.activityCodes ||
            source?.activityCodesExclude ||
            filter?.filters.some((item) => activityCodesNames.includes(item.name)),
    });

    const milestoneFragnetId = useMemo(() => {
        if (widgetId && source) {
            return source.milestoneFragnet;
        }
        if (widgetId && filter) {
            const milestoneFragnet = filter.filters.find((item) => fragnetMilestoneNames.includes(item.name));
            return milestoneFragnet?.data?.[0];
        }
    }, [filter, source]);

    const { data: milestoneFragnet } = useQueryGetSingleMilestoneFragnet({
        latestVersion: latestVersionId,
        taskVersionHashCode: milestoneFragnetId, // make sure BE can perform search by task_version_hash_code
    });

    const wbsId = useMemo(() => {
        if (widgetId && source) {
            return source.wbs;
        }
        if (widgetId && filter) {
            const wbs = filter.filters.find((item) => wbsNames.includes(item.name));
            return wbs?.data?.[0];
        }
    }, [filter, source]);

    const { data: wbs } = useQueryGetSingleWbs({
        latestVersion: latestVersionId,
        taskId: wbsId, // make sure BE can perform search by task_version_hash_code
    });

    const { data: resourceOptions } = useQueryGetResourceListForChart({
        latestVersion: latestVersionId,
    });

    return { activityCodes, milestoneFragnet, wbs, resourceOptions, assigneeOptionsList, clusters };
};
